import { useEffect, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";

import { ProviderButton, type Provider } from "@/components/ui";
import { api } from "@/lib/api";

import { MarketingShell } from "./MarketingShell";
import { apiBaseUrl } from "../../lib/env";

const route = getRouteApi("/sign-in");

/** Every door the platform knows how to build, in fixed order. What a deployment can actually
 * open is a subset of this and comes from the api, never from here. */
const PROVIDERS: readonly Provider[] = [
  "google",
  "discord",
  "facebook",
  "twitch",
  "kick",
];

interface SignInProviders {
  readonly providers: readonly string[];
}

/**
 * Which doors this deployment can open.
 *
 * Held here once and drawn from, because the page used to state the five the platform supports and
 * a deployment declares its own. On nebulize.app that was four buttons answering 503 on press, and
 * a person with no working provider had no way to tell which one was theirs. The list the api
 * publishes is the only one that is true of the deployment being looked at.
 */
function useSignInProviders() {
  return useQuery({
    queryKey: ["auth", "providers"],
    queryFn: () => api.get<SignInProviders>("/v1/auth/providers"),
    staleTime: 5 * 60 * 1000,
  });
}

const NAME: Record<Provider, string> = {
  google: "Google",
  discord: "Discord",
  facebook: "Facebook",
  twitch: "Twitch",
  kick: "Kick",
};

const LAST_USED = "nebulize.last_provider";

function isProvider(value: string | null | undefined): value is Provider {
  return PROVIDERS.includes(value as Provider);
}

function lastUsed(): Provider | undefined {
  try {
    const stored = window.localStorage.getItem(LAST_USED);
    return isProvider(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

/** One sentence, identical for every cause except a named provider refusing to take sign-ins. */
function sentenceFor(error: string, provider: string | undefined): string {
  if (error === "provider_rate_limited" && isProvider(provider)) {
    return `${NAME[provider]} is not taking sign-ins right now.`;
  }
  return "That sign-in did not complete.";
}

/** Sign in, per `docs/ui/auth/sign-in.md`. Five OAuth doors, and no password anywhere. */
export function SignIn() {
  const search = route.useSearch();
  const [hint, setHint] = useState<Provider | undefined>(undefined);
  const alert = useRef<HTMLParagraphElement>(null);
  const offered = useSignInProviders();

  /** Everything this deployment serves, in the platform's order. Empty until the read lands. */
  const doors = PROVIDERS.filter(
    (one) => offered.data?.providers.includes(one) ?? false,
  );

  useEffect(() => {
    setHint(lastUsed());
  }, []);

  useEffect(() => {
    if (search.error !== undefined) alert.current?.focus();
  }, [search.error]);

  const start = (provider: Provider) => {
    try {
      window.localStorage.setItem(LAST_USED, provider);
    } catch {
      // A browser refusing storage costs the hint and nothing else.
    }
    // Absolute, and on the api's own origin. Sign-in is a top-level navigation to the host
    // that sets the flow cookie and that the provider returns to; a relative path lands on
    // the dashboard host, which forwards nothing to the api.
    window.location.assign(`${apiBaseUrl}/v1/auth/${provider}/start`);
  };

  return (
    <MarketingShell>
      <section className="mk-auth">
        <h1 className="mk-h2">Sign in</h1>

        {search.error === undefined ? null : (
          <p className="mk-alert" role="alert" tabIndex={-1} ref={alert}>
            {sentenceFor(search.error, search.provider)}
          </p>
        )}

        {hint === undefined ? null : (
          <p className="mk-hint">Last used: {NAME[hint]}</p>
        )}

        <div className="mk-providers">
          {doors.map((provider) => (
            <ProviderButton
              key={provider}
              provider={provider}
              action="Sign in"
              onClick={() => start(provider)}
            />
          ))}
        </div>

        {offered.isPending || doors.length > 0 ? null : (
          <p className="mk-alert" role="alert">
            This deployment has no sign-in provider configured.
          </p>
        )}
      </section>
    </MarketingShell>
  );
}
